<template>
	<common-card title="MemberPress Settings" type="minimize" default-status="min">
		<template #title>
			<span class="advanced-settings-card-title">
				MemberPress Settings
				<span class="advanced-settings-enabled-flag" :class="{ 'advanced-settings-enabled': hasMbpSettings }">
					Enabled
				</span>
			</span>
		</template>
		<div
			class="omapi-memberpress-settings"
			:class="`omapi-memberpress-settings__${memberpress.isActive ? 'active' : 'inactive'}`"
		>
			<template v-if="memberpress.isActive">
				<campaigns-tooltip-label
					v-for="(setting, index) in getSettingOptions('memberships')"
					:key="setting.id + index"
					:tooltip="setting.tooltip"
					:label="setting.label"
					:disabled="false"
				>
					<core-form-multi-select-input
						:name="setting.name"
						:value="getSettingValue(setting)"
						:options="getFormattedOptions(setting.options)"
						:disabled="false"
						class="omapi-input"
						search-placeholder="Type to search"
						@input="setMultiSelectValue"
					/>
				</campaigns-tooltip-label>
				<campaigns-tooltip-label
					tooltip="Show if the user is on any of the following pages."
					label="Show on MemberPress pages:"
					class="membership-pages-select"
				>
					<div v-for="page in globalPageOptions" :key="page.value" class="omapi-radio-group">
						<common-selectable-input
							type="checkbox"
							:class="{
								'text-setting': true,
								'option-hidden': isDisabled(page.value),
							}"
							:value="getPagesValue(page)"
							:checked="isChecked(page)"
							:disabled="isDisabled(page.value)"
							:name="page.value"
							:label="getGlobalLabel(page)"
							@input="updateGlobalPage"
						/>
					</div>
				</campaigns-tooltip-label>
				<campaigns-tooltip-label
					v-for="(setting, index) in getSettingOptions('pages')"
					:key="setting.id + index"
					:tooltip="setting.tooltip"
					:label="setting.label"
					:class="{
						'option-hidden': isDisabled(setting.id),
					}"
					:disabled="false"
				>
					<core-form-multi-select-input
						:name="setting.name"
						:value="getSettingValue(setting)"
						:options="getFormattedOptions(setting.options)"
						:disabled="isDisabled(setting.id)"
						class="omapi-input"
						search-placeholder="Type to search"
						@input="setMultiSelectValue"
					/>
				</campaigns-tooltip-label>
				<div v-if="memberpress.isCoursesActive">
					<core-alert v-if="memberpress.wpFooterDisabled" type="warn">
						<div class="alert-message">
							The following settings are not available unless the <strong>WP Footer Hook</strong> setting
							in the <br /><a :href="$urls.wpAdmin('?page=memberpress-options#mepr-design')"
								>MemberPress Courses settings</a
							>
							is set to <strong>Enabled</strong>.
						</div>
					</core-alert>
					<campaigns-tooltip-label
						v-for="(setting, index) in getSettingOptions('addOns')"
						:key="setting.id + index"
						:tooltip="setting.tooltip"
						:label="setting.label"
						:disabled="memberpress.wpFooterDisabled"
					>
						<core-form-multi-select-input
							:name="setting.name"
							:value="getSettingValue(setting)"
							:options="getFormattedOptions(setting.options)"
							:disabled="memberpress.wpFooterDisabled"
							class="omapi-input"
							search-placeholder="Type to search"
							@input="setMultiSelectValue"
						/>
					</campaigns-tooltip-label>
				</div>
			</template>
		</div>
		<slot />
	</common-card>
</template>

<script lang="js">
import { mapGetters } from 'vuex'
import { campaign } from '@/campaigns/mixins/campaign'
import { mpSettings } from '@/campaigns/data'
import { isTruthy } from '../../utils'

export default  {
	mixins : [ campaign ],
	props  : {
		campaign : {
			type     : Object,
			required : true,
		},
	},
	computed : {
		...mapGetters('wp', [ 'memberpress' ]),
		/**
		 * Return all of the MemberPress state data.
		 *
		 * @returns {Object} The compiled data.
		 */
		values () {
			const values = {}

			Object.entries(mpSettings).forEach((setting) => {
				const [ , options ] = setting;

				options.forEach(option => {
					const key = `campaign.wp.post_meta._omapi_${option.id}`
					const value = this.$get(key);

					values[option.id] = value;
				})
			});

			return values
		},
		/**
		 * Get the "global" page options formatted for our selectable input.
		 *
		 * @returns {Array} The array of global page options.
		 */
		globalPageOptions () {
			const globalOptions = mpSettings['globalPages'] || [];

			return globalOptions.map((p) => {
				return {
					value: p.id,
					label: p.optionLabel,
				};
			});
		},
		/**
		 * Determine if any MemberPress settings have been enabled.
		 *
		 * @returns {boolean} True if settings are enabled.
		 */
		hasMbpSettings () {
			return Object.values(this.values).some(value => isTruthy(value));
		},
	},
	methods : {
		/**
		 * Returns the options for the given setting type.
		 *
		 * @param {string} type The setting type.
		 *
		 * @returns {Array} The compiled options.
		 */
		getSettingOptions (type) {
			return mpSettings[type] || [];
		},
		/**
		 * Returns options formatted for multi-select inputs.
		 *
		 * @param {string} type The setting type.
		 *
		 * @returns {Array} The formatted options.
		 */
		getFormattedOptions (type) {
			const options = this.memberpress[type];

			if(!options) {
				return [];
			}

			return options.map(entity => {
				const value = entity.value ? entity.value.toString() : '';

				return {
					...entity,
					name: entity.label + (value ? ` (${value})` : ''),
					fancyLabel: entity.label + (value ? ` <em>(${value})</em>` : ''),
					value,
				};
			});
		},
		/**
		 * Get the current value (in state) for the given setting.
		 *
		 * @param {string} setting The desired setting.
		 *
		 * @returns {mixed} The setting value.
		 */
		getSettingValue (setting) {
			return this.values[setting.id] || setting.default;
		},
		/**
		 * Get the current value (in state) for the give global page.
		 *
		 * @param {string} page The page id/slug.
		 *
		 * @returns {boolean} True if the value is '1'.
		 */
		getPagesValue (page) {
			return '1' === this.values[page.value] ? '1' : '0'
		},
		/**
		 * Get the label for the "global" options.
		 *
		 * @param {Object} page The page setting object.
		 *
		 * @returns {string} The option label.
		 */
		getGlobalLabel (page) {
			// If checkout is disabled, add /Checkout to the Register label text.
			return 'Register' === page.label && this.isDisabled('is_mbp_checkout') ? `${page.label}/Checkout` : page.label;
		},
		/**
		 * Determine if the given global page is checked/selected.
		 *
		 * @param {string} page The page id/slug.
		 *
		 * @returns {boolean} True if selected.
		 */
		isChecked (page) {
			return !!parseInt(this.getPagesValue(page))
		},
		/**
		 * Determine if the given id should be disabled.
		 *
		 * @param {string} id The id for the page/setting.
		 *
		 * @returns {boolean} True if id should be disabled.
		 */
		isDisabled (id) {
			// When MemberPress Pro templates are being used for registration pages, this
			// combines the registration and checkout pages into one. Therefore, the checkout
			// options don't work, so let's disable and hide them.
			// @link https://docs.memberpress.com/article/415-customizing-the-registration-page-with-readylaunch
			if('is_mbp_checkout' === id || 'show_on_mbp_checkout_pages' === id) {
				return Boolean(this.memberpress.checkoutTemplateEnabled);
			}

			return false;
		},
		/**
		 * Update the value for the given global page.
		 *
		 * @param {string} page The page id/slug.
		 *
		 * @returns {void}
		 */
		updateGlobalPage ({ target }) {
			const { name : id } = target

			const value = '1' === this.values[id] ? '0' : '1'

			this.updateValue(id, value)
		},
		/**
		 * Update the value for the setting.
		 *
		 * @todo this is currently only being used for global page checkboxes, might want to consolidate.
		 *
		 * @param {string} id The campaign id.
		 * @param {mixed} value The value.
		 * @param {string} valueType The value type.
		 *
		 * @returns {void}
		 */
		updateValue (id, value, valueType = 'numericBool') {
			this.updateCampaignValue({
				campaignId : this.id,
				key        : `wp.post_meta._omapi_${id}`,
				value,
				valueType,
			})
		},
	},
}
</script>
<style>
.membership-pages-select .setting-label {
	margin-bottom: 10px;
}

.option-hidden {
	display: none !important;
}
</style>
